import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { fetchRoutesByCountry, fetchCurvyRoutes } from '../../services/publishService';
import { fetchToursByCountry } from '../../services/tourService';
import { CurvinessBadge } from '../common/CurvinessBadge';
import { formatDistance } from '../../utils/formatters';
import './CategoryPage.css';

// kind: 'country' | 'curvy'
export function CategoryPage({ kind, slug, title, onClose, onOpenRoute, onOpenTour }) {
  const [routes, setRoutes] = useState(null);
  const [tours, setTours] = useState(null);

  useEffect(() => {
    if (kind === 'curvy') {
      fetchCurvyRoutes().then(setRoutes).catch(() => setRoutes([]));
      setTours([]);
    } else {
      fetchRoutesByCountry(slug).then(setRoutes).catch(() => setRoutes([]));
      fetchToursByCountry(slug).then(setTours).catch(() => setTours([]));
    }
  }, [kind, slug]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const loading = routes === null || tours === null;
  const empty = !loading && routes.length === 0 && tours.length === 0;

  const RouteRow = ({ r }) => (
    <li className="cat-row" onClick={() => onOpenRoute(r)}>
      {r.imageUrls?.[0]
        ? <img className="cat-row__thumb" src={r.imageUrls[0]} alt="" loading="lazy" />
        : <div className="cat-row__thumb cat-row__thumb--ph">{r.name?.[0]?.toUpperCase()}</div>}
      <div className="cat-row__info">
        <span className="cat-row__name">{r.name}</span>
        <div className="cat-row__meta">
          {r.distance != null && <span>{formatDistance(r.distance)}</span>}
          <CurvinessBadge geometry={r.geometry} variant="inline" />
        </div>
      </div>
    </li>
  );

  const TourRow = ({ t }) => {
    const d = t.startDate?.toDate ? t.startDate.toDate() : null;
    return (
      <li className="cat-row" onClick={() => onOpenTour(t)}>
        {t.imageUrls?.[0]
          ? <img className="cat-row__thumb" src={t.imageUrls[0]} alt="" loading="lazy" />
          : <div className="cat-row__thumb cat-row__thumb--ph">{t.name?.[0]?.toUpperCase()}</div>}
        <div className="cat-row__info">
          <span className="cat-row__name">{t.name}</span>
          <div className="cat-row__meta">
            <span>{d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Date TBD'}</span>
            <span>
              {t.registrationType !== 'paid' ? 'Free'
                : t.category === 'lesson' ? `$${Number(t.hourlyRate || 0).toFixed(0)}/hr`
                : `$${Number(t.ticketPrice || 0).toFixed(0)}`}
            </span>
          </div>
        </div>
      </li>
    );
  };

  return createPortal(
    <div className="cat-overlay" onClick={e => e.target.classList.contains('cat-overlay') && onClose()}>
      <aside className="cat-panel">
        <div className="cat-header">
          <button className="cat-back" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          </button>
          <h2 className="cat-title">{title}</h2>
        </div>

        <div className="cat-body">
          {loading && <div className="cat-loading"><span className="cat-spinner" /> Loading…</div>}
          {empty && <div className="cat-empty">Nothing here yet — be the first to publish!</div>}

          {!loading && tours.length > 0 && (
            <div className="cat-section">
              <div className="cat-section__title">Tours <span>{tours.length}</span></div>
              <ul className="cat-list">{tours.map(t => <TourRow key={t.id} t={t} />)}</ul>
            </div>
          )}
          {!loading && routes.length > 0 && (
            <div className="cat-section">
              <div className="cat-section__title">Routes <span>{routes.length}</span></div>
              <ul className="cat-list">{routes.map(r => <RouteRow key={r.id} r={r} />)}</ul>
            </div>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
}
